import React, { useEffect, useMemo, useState } from "react";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../api/client";
import { extractFilterListPayload } from "../../hooks/useAdvancedSearchFilterOptions";
import { useCachedFilterOptions } from "../../hooks/useCachedFilterOptions";
import {
  EMPTY_CAMPAIGN_FILTER_STATE,
  getCampaignStatusOptions,
  normalizeCampaignFilterState,
} from "../../utils/dashboardCampaignFilterState";
import {
  mergeWhatsappFilterOptions,
  splitWhatsappFilterSelection,
  whatsappKeysFromFilterState,
} from "../../utils/whatsappCampaignFilterOptions";
import DashboardSavedFiltersPanel from "./DashboardSavedFiltersPanel";
import {
  DashboardFilterModalActions,
  DashboardFilterModalFrame,
  DashboardFilterSection,
  DashboardMultiPickList,
} from "./dashboardFilterModalUi";

async function fetchCampaignFilterOptions() {
  const results = await Promise.allSettled([
    fetchData("/channels?limit=200", "GET"),
    fetchData("/campaigns/broadcasts", "GET"),
    fetchData("/campaigns/whatsapp", "GET"),
  ]);
  const [ch, br, wa] = results.map((r) => (r.status === "fulfilled" ? r.value : null));
  const broadcastList = extractFilterListPayload(br);
  const legacyOrMerged = extractFilterListPayload(wa);
  const fromApi = mergeWhatsappFilterOptions({
    broadcasts: legacyOrMerged.filter((c) => c?.filterTarget === "campaign"),
    legacyWhatsapp: legacyOrMerged.filter(
      (c) => !c?.filterTarget || c.filterTarget === "whatsapp_campaign"
    ),
  });
  return {
    channels: extractFilterListPayload(ch),
    broadcasts: broadcastList,
    whatsappOptions: fromApi.length
      ? fromApi
      : mergeWhatsappFilterOptions({
          broadcasts: broadcastList,
          legacyWhatsapp: legacyOrMerged,
        }),
  };
}

/**
 * Modal: status das solicitações + canal + campanhas (broadcast e WhatsApp).
 */
export default function DashboardCampaignFiltersModal({
  open,
  onClose,
  initialState = EMPTY_CAMPAIGN_FILTER_STATE,
  onApply,
}) {
  const { t } = useTranslation("settings");
  const { data: options, isLoading: loading } = useCachedFilterOptions(
    "dashboard-campanhas",
    fetchCampaignFilterOptions,
    { enabled: open }
  );
  const channels = options?.channels || [];
  const broadcasts = options?.broadcasts || [];
  const whatsappOptions = options?.whatsappOptions || [];
  const [statusIds, setStatusIds] = useState([]);
  const [originIds, setOriginIds] = useState([]);
  const [campaignIds, setCampaignIds] = useState([]);
  const [whatsappKeys, setWhatsappKeys] = useState([]);
  const prevOpen = React.useRef(false);

  const statusOptions = useMemo(() => getCampaignStatusOptions(), []);

  useEffect(() => {
    if (!open) {
      prevOpen.current = false;
      return;
    }
    const opening = !prevOpen.current;
    prevOpen.current = true;
    if (opening) {
      const s = normalizeCampaignFilterState(initialState);
      setStatusIds(s.ids_status);
      setOriginIds(s.ids_origens);
      setCampaignIds(s.ids_campaigns);
    }
  }, [open, initialState]);

  useEffect(() => {
    if (!open || loading) return;
    const s = normalizeCampaignFilterState(initialState);
    setWhatsappKeys(
      whatsappKeysFromFilterState(
        {
          ids_campaigns: s.ids_campaigns,
          ids_whatsapp_campaigns: s.ids_whatsapp_campaigns,
        },
        whatsappOptions
      )
    );
  }, [open, loading, initialState, whatsappOptions]);

  const buildLabels = () => {
    const statuses = {};
    statusOptions.forEach((o) => {
      statuses[String(o.id)] = o.label;
    });
    const origins = {};
    channels.forEach((c) => {
      const id = Number(c?.id);
      if (!id) return;
      origins[String(id)] = String(c?.nome || c?.name || `#${id}`);
    });
    const campaigns = {};
    broadcasts.forEach((c) => {
      const id = Number(c?.id);
      if (!id) return;
      campaigns[String(id)] = String(c?.name || c?.nome || `#${id}`);
    });
    const whatsapp = {};
    whatsappOptions.forEach((c) => {
      if (!c?.filterKey) return;
      whatsapp[c.filterKey] = String(c?.name || c?.nome || c.filterKey);
      if (c.filterTarget === "whatsapp_campaign") {
        whatsapp[String(c.id)] = String(c?.name || c?.nome || `#${c.id}`);
      }
      if (c.filterTarget === "campaign") {
        campaigns[String(c.id)] = String(c?.name || c?.nome || `#${c.id}`);
      }
    });
    return { statuses, origins, campaigns, whatsappCampaigns: whatsapp };
  };

  const resolvedSelection = useMemo(
    () => splitWhatsappFilterSelection(whatsappKeys, whatsappOptions),
    [whatsappKeys, whatsappOptions]
  );

  const handleClear = () => {
    setStatusIds([]);
    setOriginIds([]);
    setCampaignIds([]);
    setWhatsappKeys([]);
  };

  const handleApply = () => {
    const labels = buildLabels();
    const mergedCampaignIds = [
      ...new Set([...(campaignIds || []), ...resolvedSelection.ids_campaigns]),
    ];
    onApply?.(
      normalizeCampaignFilterState({
        ids_status: statusIds,
        ids_origens: originIds,
        ids_campaigns: mergedCampaignIds,
        ids_whatsapp_campaigns: resolvedSelection.ids_whatsapp_campaigns,
        labels,
      })
    );
    onClose?.();
  };

  const hasActive =
    statusIds.length > 0 ||
    originIds.length > 0 ||
    campaignIds.length > 0 ||
    whatsappKeys.length > 0;

  return (
    <DashboardFilterModalFrame
      open={open}
      onClose={onClose}
      icon={<CampaignOutlinedIcon />}
      title={t("dashboardPage.campaignFilters.title", { defaultValue: "Filtrar campanhas" })}
      subtitle={t("dashboardPage.campaignFilters.subtitle", {
        defaultValue: "Os gráficos desta aba passam a refletir só estes canais e campanhas",
      })}
      loading={loading}
      footer={
        <DashboardFilterModalActions
          onClear={handleClear}
          onClose={onClose}
          onApply={handleApply}
          hasActive={hasActive}
          clearLabel={t("dashboardPage.campaignFilters.clearAll", { defaultValue: "Limpar" })}
          cancelLabel={t("dashboardPage.campaignFilters.cancel", { defaultValue: "Cancelar" })}
          applyLabel={t("dashboardPage.campaignFilters.apply", { defaultValue: "Aplicar" })}
        />
      }
    >
      <DashboardFilterSection
        title={t("dashboardPage.campaignFilters.status", {
          defaultValue: "Status das solicitações",
        })}
        hint={t("dashboardPage.campaignFilters.statusHint", {
          defaultValue: "Quais status entram nos gráficos desta aba",
        })}
        selectedCount={statusIds.length}
      >
        <DashboardMultiPickList
          options={statusOptions}
          value={statusIds}
          onChange={setStatusIds}
          getLabel={(o) => o.label}
          getValue={(o) => o.id}
          searchPlaceholder={t("dashboardPage.campaignFilters.searchStatus", {
            defaultValue: "Buscar status…",
          })}
          emptyLabel={t("dashboardPage.campaignFilters.emptyStatus", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.campaignFilters.origins", { defaultValue: "Canal" })}
        hint={t("dashboardPage.campaignFilters.originsHint", {
          defaultValue: "Origem do lead",
        })}
        selectedCount={originIds.length}
      >
        <DashboardMultiPickList
          options={channels}
          value={originIds}
          onChange={setOriginIds}
          getLabel={(o) => o?.nome || o?.name || `#${o?.id}`}
          getValue={(o) => o?.id}
          searchPlaceholder={t("dashboardPage.campaignFilters.searchOrigins", {
            defaultValue: "Buscar canais…",
          })}
          emptyLabel={t("dashboardPage.campaignFilters.emptyOrigins", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.campaignFilters.broadcasts", { defaultValue: "Broadcast" })}
        hint={t("dashboardPage.campaignFilters.broadcastsHint", {
          defaultValue: "Campanhas omnichannel",
        })}
        selectedCount={campaignIds.length}
      >
        <DashboardMultiPickList
          options={broadcasts}
          value={campaignIds}
          onChange={setCampaignIds}
          getLabel={(o) => o?.name || o?.nome || `#${o?.id}`}
          getValue={(o) => o?.id}
          searchPlaceholder={t("dashboardPage.campaignFilters.searchCampaigns", {
            defaultValue: "Buscar campanhas…",
          })}
          emptyLabel={t("dashboardPage.campaignFilters.emptyCampaigns", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.campaignFilters.whatsapp", { defaultValue: "WhatsApp" })}
        hint={t("dashboardPage.campaignFilters.whatsappHint", {
          defaultValue: "Disparos WhatsApp (envio ativo e legado)",
        })}
        selectedCount={whatsappKeys.length}
      >
        <DashboardMultiPickList
          options={whatsappOptions}
          value={whatsappKeys}
          onChange={setWhatsappKeys}
          getLabel={(o) => o?.name || o?.nome || o?.filterKey || `#${o?.id}`}
          getValue={(o) => o?.filterKey || o?.id}
          valueAsString
          searchPlaceholder={t("dashboardPage.campaignFilters.searchCampaigns", {
            defaultValue: "Buscar campanhas…",
          })}
          emptyLabel={t("dashboardPage.campaignFilters.emptyWhatsapp", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      {!loading ? (
        <DashboardSavedFiltersPanel
          section="origem"
          getSnapshot={() => {
            const labels = buildLabels();
            const mergedCampaignIds = [
              ...new Set([...(campaignIds || []), ...resolvedSelection.ids_campaigns]),
            ];
            return normalizeCampaignFilterState({
              ids_status: statusIds,
              ids_origens: originIds,
              ids_campaigns: mergedCampaignIds,
              ids_whatsapp_campaigns: resolvedSelection.ids_whatsapp_campaigns,
              labels,
            });
          }}
          onApply={(state) => {
            onApply?.(state);
            onClose?.();
          }}
          canSave
        />
      ) : null}
    </DashboardFilterModalFrame>
  );
}
